import { useState, useEffect, useRef, useCallback } from 'react'

export function useVideoPlayer({
  isVideo,
  videoUrl,
  containerRef,
  autoplay = false
}) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isMuted, setIsMuted] = useState(false)
  const [showPlayerControls, setShowPlayerControls] = useState(true)
  const [showSeekbar, setShowSeekbar] = useState(true)
  const [isLandscape, setIsLandscape] = useState(false)

  const controlsTimeoutRef = useRef(null)
  const initialAutoplayTimeoutRef = useRef(null)
  const initialAutoplayDoneRef = useRef(false)
  const prevHtmlOverflowRef = useRef('')
  const prevBodyOverflowRef = useRef('')
  const prevBodyWidthRef = useRef('')
  const videoSelector = '.content-detail-player video'

  const getVideoElement = useCallback(() => {
    if (containerRef && containerRef.current) {
      const video = containerRef.current.querySelector('video')
      if (video) return video
    }

    const selectors = [
      videoSelector,
      '.content-detail-player video',
      'video',
      '#logixPlayer video',
      '.mainVideoContainer video'
    ]

    for (const selector of selectors) {
      const video = document.querySelector(selector)
      if (video) {
        return video
      }
    }

    if (window.splashPlayerObj && window.splashPlayerObj.player) {
      const logixVideo = window.splashPlayerObj.player.querySelector('video')
      if (logixVideo) {
        return logixVideo
      }
    }

    if (window.splashPlayerObj && window.splashPlayerObj.media) {
      return window.splashPlayerObj.media
    }

    if (window.splashPlayerObj && window.splashPlayerObj.el) {
      return window.splashPlayerObj.el
    }

    const allVideos = document.querySelectorAll('video')
    if (allVideos.length > 0) {
      return allVideos[0]
    }

    return null
  }, [videoSelector])

  const createFallbackVideo = useCallback(() => {
    if (!isVideo || !videoUrl) return null

    const existingVideo = getVideoElement()
    if (existingVideo) return existingVideo

    const video = document.createElement('video')
    video.src = videoUrl
    video.muted = isMuted
    video.loop = true
    video.style.position = 'absolute'
    video.style.top = '0'
    video.style.left = '0'
    video.style.width = '100%'
    video.style.height = '100%'
    video.style.objectFit = 'cover'
    video.style.zIndex = '0'

    const playerContainer =
      (containerRef && containerRef.current) ||
      document.querySelector('.content-detail-player') ||
      document.querySelector('#logixPlayer') ||
      document.querySelector('#playerDiv')

    if (playerContainer) {
      playerContainer.appendChild(video)
      return video
    }

    return null
  }, [isVideo, videoUrl, isMuted, getVideoElement])

  const showControlsWithTimeout = useCallback(() => {
    setShowPlayerControls(true)
    setShowSeekbar(true)
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current)
      controlsTimeoutRef.current = null
    }
    controlsTimeoutRef.current = setTimeout(() => {
      const videoElement = getVideoElement()
      if (videoElement && !videoElement.paused) {
        setShowPlayerControls(false)
        setShowSeekbar(false)
      }
    }, 4000)
  }, [getVideoElement])

  const reconcileVideoState = useCallback(() => {
    const videoElement = getVideoElement()
    if (!videoElement) return
    setIsPlaying(!videoElement.paused)
  }, [getVideoElement])

  const handleTimeUpdate = () => {
    const video = getVideoElement()
    if (video && !isNaN(video.currentTime)) {
      setCurrentTime(video.currentTime)
    }
  }

  const handleLoadedMetadata = () => {
    const video = getVideoElement()
    if (video && !isNaN(video.duration) && video.duration > 0) {
      setDuration(video.duration)
    }
  }

  const handleMetaData = () => {
    const video = getVideoElement()
    const landscape = video?.videoWidth > video?.videoHeight || false
     setIsLandscape(landscape)
    const allVideos = document.querySelectorAll('video')
    handleLoadedMetadata()
    if (allVideos?.length > 1) {
      allVideos.forEach((v) => {
        if (v !== video) {
          v.pause()
        }
      })
    }
  }

  const togglePlayPause = useCallback(
    (e) => {
      if (e) e.stopPropagation()
      const video = getVideoElement()
      if (!video) return
      try {
        if (!video.paused) {
          video.pause()

          const playerContainers = [
            (containerRef && containerRef.current),
            document.querySelector('.content-detail-player'),
            document.querySelector('#logixPlayer'),
            document.querySelector('#playerDiv')
          ].filter(Boolean)

          playerContainers.forEach((container) => {
            container.querySelectorAll('video, audio').forEach((el) => {
              try { el.pause() } catch (err) {}
            })
          })

          if (window.splashPlayerObj) {
            try {
              if (window.splashPlayerObj.player && typeof window.splashPlayerObj.player.pause === 'function') {
                window.splashPlayerObj.player.pause()
              }
              if (window.splashPlayerObj.media && typeof window.splashPlayerObj.media.pause === 'function') {
                window.splashPlayerObj.media.pause()
              }
              if (typeof window.splashPlayerObj.pause === 'function') {
                window.splashPlayerObj.pause()
              }
            } catch (err) {}
          }
          setIsPlaying(false)
          if (initialAutoplayTimeoutRef.current) {
            clearTimeout(initialAutoplayTimeoutRef.current)
            initialAutoplayTimeoutRef.current = null
          }
          initialAutoplayDoneRef.current = true
        } else {
          const allVideos = document.querySelectorAll('video')
          if (allVideos?.length > 1) {
            allVideos.forEach((v) => {
              if (v !== video) {
                v.pause()
              }
            })
          }
          const handlePlaySuccess = () => {
            setIsPlaying(true)
            showControlsWithTimeout()
          }
          const handlePlayError = (err) => {
            if (err && err.name === 'AbortError') {
              console.debug('Play was interrupted, safe to ignore.')
              return
            }
            console.error('Video play failed:', err)
            setShowPlayerControls(true)
            setShowSeekbar(true)
            setIsPlaying(false)
          }

          if (typeof window.playSplashVideo === 'function') {
            window.playSplashVideo()
              .then(handlePlaySuccess)
              .catch(() => {
                const playPromise = video.play()
                if (playPromise !== undefined) {
                  playPromise.then(handlePlaySuccess).catch(handlePlayError)
                }
              })
          } else {
            const playPromise = video.play()
            if (playPromise !== undefined) {
              playPromise.then(handlePlaySuccess).catch(handlePlayError)
            }
          }
        }
        showControlsWithTimeout()
      } catch (err) {
        console.error('PLAY/PAUSE ERROR:', err)
      }
    },
    [getVideoElement, showControlsWithTimeout, containerRef]
  )

  const seekForward = useCallback(
    (e) => {
      if (e) e.stopPropagation()
      const video = getVideoElement()
      if (!video) return
      const newTime = Math.min(video.currentTime + 10, duration)
      video.currentTime = newTime
      setCurrentTime(newTime)
      showControlsWithTimeout()
      reconcileVideoState()
    },
    [getVideoElement, duration, showControlsWithTimeout, reconcileVideoState]
  )

  const seekBackward = useCallback(
    (e) => {
      if (e) e.stopPropagation()
      const video = getVideoElement()
      if (!video) return
      const newTime = Math.max(video.currentTime - 10, 0)
      video.currentTime = newTime
      setCurrentTime(newTime)
      showControlsWithTimeout()
      reconcileVideoState()
    },
    [getVideoElement, showControlsWithTimeout, reconcileVideoState]
  )

  const toggleMute = useCallback(
    (e) => {
      if (e) e.stopPropagation()
      const video = getVideoElement()
      const newMuted = video ? !video.muted : !isMuted

      if (video) {
        video.muted = newMuted
      }

      const playerContainers = [
        (containerRef && containerRef.current),
        document.querySelector('.content-detail-player'),
        document.querySelector('#logixPlayer'),
        document.querySelector('#playerDiv')
      ].filter(Boolean)

      playerContainers.forEach((container) => {
        container.querySelectorAll('video, audio').forEach((el) => {
          try { el.muted = newMuted } catch (err) {}
        })
      })

      if (window.splashPlayerObj?.player && typeof window.splashPlayerObj.player.muted === 'function') {
        try { window.splashPlayerObj.player.muted(newMuted) } catch (err) {}
      }

      setIsMuted(newMuted)
      showControlsWithTimeout()
      reconcileVideoState()
    },
    [getVideoElement, isMuted, showControlsWithTimeout, reconcileVideoState, containerRef]
  )

  const handleSeek = useCallback(
    (e) => {
      const video = getVideoElement()
      const currentDuration = video?.duration || duration
      if (!video || !currentDuration || isNaN(currentDuration) || currentDuration <= 0) {
        return
      }
      
      e.stopPropagation()
      const rect = e.currentTarget.getBoundingClientRect()
      
      // Handle both mouse and touch events
      const clientX = e.clientX !== undefined 
        ? e.clientX 
        : (e.touches && e.touches[0] ? e.touches[0].clientX : (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : 0))
      
      const clickX = clientX - rect.left
      const seekTime = Math.max(
        0,
        Math.min((clickX / rect.width) * currentDuration, currentDuration)
      )
      
      if (!isNaN(seekTime) && isFinite(seekTime)) {
        video.currentTime = seekTime
        setCurrentTime(seekTime)
        showControlsWithTimeout()
      }
    },
    [getVideoElement, duration, showControlsWithTimeout]
  )

  useEffect(() => {
    const setupEventListeners = () => {
      const video = getVideoElement()
      if (!video) {
        return null
      }

      const handlePlay = () => {
        setIsPlaying(true)
        showControlsWithTimeout()
      }

      const handlePause = () => {
        setIsPlaying(false)
        setShowPlayerControls(true)
        setShowSeekbar(true)
        if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current)
      }

      const handlePlaying = () => {
        setIsPlaying(true)
      }

      video.addEventListener('loadedmetadata', handleMetaData)
      video.addEventListener('timeupdate', handleTimeUpdate)
      video.addEventListener('play', handlePlay)
      video.addEventListener('pause', handlePause)
      video.addEventListener('playing', handlePlaying)
      video.addEventListener('durationchange', handleLoadedMetadata)
      video.addEventListener('canplay', handleLoadedMetadata)
      video.addEventListener('loadeddata', handleLoadedMetadata)
      video.addEventListener('progress', handleLoadedMetadata)

      // If video already has metadata, trigger the handler
      if (video.readyState >= 1) {
        handleLoadedMetadata()
      }

      return {
        video,
        handlePlay,
        handlePause,
        handlePlaying
      }
    }

    let listeners = setupEventListeners()

    // If no video element found, retry after a short delay
    if (!listeners) {
      const retryTimeout = setTimeout(() => {
        listeners = setupEventListeners()
      }, 1000)

      return () => {
        clearTimeout(retryTimeout)
        if (listeners) {
          const { video, handlePlay, handlePause, handlePlaying } = listeners
          video.removeEventListener('loadedmetadata', handleMetaData)
          video.removeEventListener('timeupdate', handleTimeUpdate)
          video.removeEventListener('play', handlePlay)
          video.removeEventListener('pause', handlePause)
          video.removeEventListener('playing', handlePlaying)
          video.removeEventListener('durationchange', handleLoadedMetadata)
          video.removeEventListener('canplay', handleLoadedMetadata)
          video.removeEventListener('loadeddata', handleLoadedMetadata)
          video.removeEventListener('progress', handleLoadedMetadata)
        }
      }
    }

    return () => {
      if (listeners) {
        const { video, handlePlay, handlePause, handlePlaying } = listeners
        video.removeEventListener('loadedmetadata', handleMetaData)
        video.removeEventListener('timeupdate', handleTimeUpdate)
        video.removeEventListener('play', handlePlay)
        video.removeEventListener('pause', handlePause)
        video.removeEventListener('playing', handlePlaying)
        video.removeEventListener('durationchange', handleLoadedMetadata)
        video.removeEventListener('canplay', handleLoadedMetadata)
        video.removeEventListener('loadeddata', handleLoadedMetadata)
        video.removeEventListener('progress', handleLoadedMetadata)
      }
    }
  }, [getVideoElement, showControlsWithTimeout])

  const handlePlayerAreaClick = useCallback(
    (e) => {
      // Ignore clicks on play/pause button
      if (
        e.target.classList.contains('play-pause-button') ||
        e.target.closest('.play-pause-button')
      ) {
        return
      }

      e.preventDefault()
      e.stopPropagation()

      if (e.detail === 1) {
        showControlsWithTimeout()
      } else if (e.detail === 2) {
        // Double-click: do nothing special (prevent accidental toggles)
      }

      reconcileVideoState()
    },
    [showControlsWithTimeout, reconcileVideoState]
  )

  useEffect(() => {
    try {
      // Lock scrolling
      prevHtmlOverflowRef.current = document.documentElement.style.overflow
      prevBodyOverflowRef.current = document.body.style.overflow
      prevBodyWidthRef.current = document.body.style.width
      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'
      document.body.style.width = '100vw'
    } catch (e) {
      /* ignore */
    }

    let originalExitFullscreen = null
    let originalRequestFullscreen = null
    let originalVideoRequestFullscreen = null
    let originalDivRequestFullscreen = null

    try {
      if (typeof document.exitFullscreen === 'function') {
        originalExitFullscreen = document.exitFullscreen.bind(document)
        document.exitFullscreen = () => {
          try {
            const hasFocus =
              typeof document.hasFocus === 'function'
                ? document.hasFocus()
                : true
            if (document.visibilityState !== 'visible' || !hasFocus) {
              return Promise.resolve()
            }
            return originalExitFullscreen()
          } catch {
            return Promise.resolve()
          }
        }
      }

      if (typeof Element.prototype.requestFullscreen === 'function') {
        originalRequestFullscreen = Element.prototype.requestFullscreen
        Element.prototype.requestFullscreen = function () {
          return Promise.resolve()
        }
      }

      if (typeof HTMLVideoElement.prototype.requestFullscreen === 'function') {
        originalVideoRequestFullscreen =
          HTMLVideoElement.prototype.requestFullscreen
        HTMLVideoElement.prototype.requestFullscreen = function () {
          return Promise.resolve()
        }
      }

      if (typeof HTMLDivElement.prototype.requestFullscreen === 'function') {
        originalDivRequestFullscreen =
          HTMLDivElement.prototype.requestFullscreen
        HTMLDivElement.prototype.requestFullscreen = function () {
          return Promise.resolve()
        }
      }
    } catch {
      // ignore
    }

    const globalDblClickBlocker = (e) => {
      if (
        e.target === document.documentElement ||
        e.target.tagName === 'VIDEO' ||
        e.target.classList.contains('content-detail-player')
      ) {
        e.preventDefault()
        e.stopPropagation()
      }
    }
    document.documentElement.addEventListener(
      'dblclick',
      globalDblClickBlocker,
      { capture: true }
    )

    return () => {
      // Restore styles
      document.documentElement.style.overflow = 'auto'
      document.body.style.overflow = 'auto'
      document.body.style.width = 'auto'

      // Restore original methods
      if (originalExitFullscreen)
        document.exitFullscreen = originalExitFullscreen
      if (originalRequestFullscreen)
        Element.prototype.requestFullscreen = originalRequestFullscreen
      if (originalVideoRequestFullscreen)
        HTMLVideoElement.prototype.requestFullscreen =
          originalVideoRequestFullscreen
      if (originalDivRequestFullscreen)
        HTMLDivElement.prototype.requestFullscreen =
          originalDivRequestFullscreen

      document.documentElement.removeEventListener(
        'dblclick',
        globalDblClickBlocker,
        { capture: true }
      )
    }
  }, [])

  useEffect(() => {
    const blockDblClick = (e) => {
      e.preventDefault()
      e.stopPropagation()
    }
    const options = { capture: true }
    let videoElement = null
    let playerContainer = null

    const setupDblClickBlockers = () => {
      videoElement = document.querySelector(videoSelector)
      playerContainer = document.querySelector('.content-detail-player')

      if (videoElement) {
        videoElement.addEventListener('dblclick', blockDblClick, options)
      }
      if (playerContainer) {
        playerContainer.addEventListener('dblclick', blockDblClick, options)
      }
    }

    // Initial setup and retry to ensure elements are mounted
    setupDblClickBlockers()
    const retryTimeout = setTimeout(setupDblClickBlockers, 500)

    return () => {
      clearTimeout(retryTimeout)
      if (videoElement) {
        videoElement.removeEventListener('dblclick', blockDblClick, options)
      }
      if (playerContainer) {
        playerContainer.removeEventListener('dblclick', blockDblClick, options)
      }
    }
  }, [videoSelector])

  useEffect(() => {
    if (!isVideo || !videoUrl) return

    // Clear any pending autoplay attempts
    if (initialAutoplayTimeoutRef.current) {
      clearTimeout(initialAutoplayTimeoutRef.current)
      initialAutoplayTimeoutRef.current = null
    }

    // Reset autoplay done flag when video URL changes
    initialAutoplayDoneRef.current = false

    // Only attempt initial autoplay once per content
    if (!initialAutoplayDoneRef.current && autoplay) {
      initialAutoplayTimeoutRef.current = setTimeout(() => {
        setShowPlayerControls(true)
        setShowSeekbar(true)

        const videoElement = getVideoElement()

        if (videoElement) {
          // Set mute state according to hook state
          videoElement.muted = isMuted

          const playPromise = videoElement.play()
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                setIsPlaying(true)
                initialAutoplayDoneRef.current = true

                // Hide controls after 4s if video is playing
                if (controlsTimeoutRef.current) {
                  clearTimeout(controlsTimeoutRef.current)
                }
                controlsTimeoutRef.current = setTimeout(() => {
                  if (videoElement && !videoElement.paused) {
                    setShowPlayerControls(false)
                    setShowSeekbar(false)
                  }
                }, 4000)
              })
              .catch((err) => {
                console.error('Autoplay failed:', err)
                // Keep controls visible if autoplay fails
                setShowPlayerControls(true)
                setShowSeekbar(true)
                setIsPlaying(false)
              })
          }
        }
      }, 500)
    }

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current)
        controlsTimeoutRef.current = null
      }
      if (initialAutoplayTimeoutRef.current) {
        clearTimeout(initialAutoplayTimeoutRef.current)
        initialAutoplayTimeoutRef.current = null
      }
      try {
        const videoElement = getVideoElement()
        if (videoElement) {
          videoElement.pause()
        }
      } catch (e) {}
    }
  }, [isVideo, videoUrl, getVideoElement])

  // Add a periodic check to ensure video element is found and state is synchronized
  useEffect(() => {
    if (!isVideo || !videoUrl) return

    const checkVideoElement = () => {
      let videoElement = getVideoElement()

      // If no video element found, wait for it to be created by the player SDK
      // Do not create a fallback video which leads to duplicate playing videos
      if (!videoElement) {
        return
      }

      if (videoElement) {
        // Sync state with video element
        if (videoElement.paused !== !isPlaying) {
          setIsPlaying(!videoElement.paused)
        }
        if (videoElement.muted !== isMuted) {
          setIsMuted(videoElement.muted)
        }
        if (Math.abs(videoElement.currentTime - currentTime) > 0.1) {
          setCurrentTime(videoElement.currentTime)
        }
        if (Math.abs(videoElement.duration - duration) > 0.1) {
          setDuration(videoElement.duration)
        }
      }
    }

    // Check immediately and then every 500ms for more responsive updates
    checkVideoElement()
    const interval = setInterval(checkVideoElement, 500)

    // Also set up a mutation observer to detect when video elements are added
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.tagName === 'VIDEO' || node.querySelector('video')) {
                checkVideoElement()
              }
            }
          })
        }
      })
    })

    // Observe the player container for changes
    const playerContainer =
      (containerRef && containerRef.current) ||
      document.querySelector('.content-detail-player') ||
      document.querySelector('#logixPlayer') ||
      document.querySelector('#playerDiv')

    if (playerContainer) {
      observer.observe(playerContainer, {
        childList: true,
        subtree: true
      })
    }

    return () => {
      clearInterval(interval)
      observer.disconnect()
    }
  }, [
    isVideo,
    videoUrl,
    isPlaying,
    isMuted,
    currentTime,
    duration,
    getVideoElement,
    createFallbackVideo
  ])

  return {
    isPlaying,
    currentTime,
    duration,
    isMuted,
    showPlayerControls,
    showSeekbar,
    isLandscape,
    togglePlayPause,
    seekForward,
    seekBackward,
    toggleMute,
    handleSeek,
    showControlsWithTimeout,
    reconcileVideoState,
    controlsTimeoutRef,
    setShowSeekbar,
    setIsPlaying,
    setCurrentTime,
    setDuration,
    setIsMuted,
    setShowPlayerControls,
    initialAutoplayTimeoutRef,
    initialAutoplayDoneRef,
    handlePlayerAreaClick,
    createFallbackVideo
  }
}
